import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import "../assets/css/biodataform.css";
import ApplicationSuccess from "./ApplicationSuccess";

const gradeOptions = ["A1", "B2", "B3", "C4", "C5", "C6", "D7", "E8", "F9"];

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
  ? "https://schools.luth.gov.ng" || "https://10.0.0.57"
  : "http://localhost:3003";

const jambEligibleProgrammes = [
  "Department of Nursing Sciences",
  "Department of Nursing Sciences",
  "School of Health Information Management (ND)",
  "Biomedical Engineering (ND)",
];

const getSep30AgeCutoff = () => {
  const applicationYear = new Date().getFullYear();
  const cutoff = new Date(applicationYear, 8, 30); // September 30 of application year
  cutoff.setFullYear(cutoff.getFullYear() - 16); // must be 16 by this date
  cutoff.setHours(23, 59, 59, 999);
  return cutoff;
};

const formatDateToYMD = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const BiodataForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    LastName: "",
    FirstName: "",
    MiddleName: "",
    Gender: "",
    DateOfBirth: "",
    Email: localStorage.getItem("email") || "",
    PhoneNumber: "",
    MaritalStatus: "",
    MaidenName: "",
    StateOfOrigin: "",
    LocalGovernment: "",
    Nationality: "",
    Religion: "",
    Denomination: "",
    PermanentAddress: "",
    NextOfKin: "",
    NextOfKinAddress: "",
    NextOfKinPhone: "",
    CoCurricularActivities: "",
    JambRegNumber: "",
    JambYear: "",
    JambExamNumber: "",
    JambScore: "",
    programme: localStorage.getItem("programme") || "",
  });

  const [dob, setDob] = useState(null);
  const [passportPhoto, setPassportPhoto] = useState(null);
  const [passportPreview, setPassportPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documents, setDocuments] = useState([{ file: null, preview: null }]);
  const [submitted, setSubmitted] = useState(false);
  const [submittedBiodata, setSubmittedBiodata] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current: "",
    new: "",
    confirm: "",
  });

  const [institutions, setInstitutions] = useState([
    {
      InstitutionName: "",
      QualificationObtained: "",
      StartYear: "",
      EndYear: "",
    },
  ]);

  const [programme, setProgramme] = useState("");

  const isMedSocial = programme === "Medical and Psychiatry Social Work";

  function makeSAG() {
    if (isMedSocial) {
      const obj = {};
      for (let i = 0; i < 8; i++) {
        obj[`subject${i}`] = "";
        obj[`grade${i}`] = "";
      }
      return obj;
    }

    return {
      English: "",
      Mathematics: "",
      Biology: "",
      Physics: "",
      Chemistry: "",
    };
  }

  const [exams, setExams] = useState([
    {
      ExamName: "",
      Sittings: 1,
      SubjectsAndGrades: makeSAG(),
    },
  ]);

  const isJambRequired = jambEligibleProgrammes.includes(programme);
  const ageCutoffDate = useMemo(() => {
    if (!isJambRequired) return null;
    return getSep30AgeCutoff();
  }, [isJambRequired]);

  const jambMin = programme === "Department of Nursing Sciences" ? 200 : 160;

  useEffect(() => {
    const prog = localStorage.getItem("programme");
    const storedEmail = localStorage.getItem("email");

    if (prog) setProgramme(prog);
    if (storedEmail) {
      setFormData((fd) => ({
        ...fd,
        Email: storedEmail,
        programme: prog || fd.programme,
      }));
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("biodata");
    if (saved) {
      setSubmittedBiodata(JSON.parse(saved));
      setSubmitted(true);
    }
  }, []);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const onPop = () =>
      window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", onPop);

    return () => {
      window.removeEventListener("popstate", onPop);
    };
  }, []);

  useEffect(() => {
    return () => {
      localStorage.removeItem("email");
      localStorage.removeItem("programme");
      localStorage.removeItem("biodata");
    };
  }, []);

  const normalize = (v) =>
    v === null || v === undefined ? "" : String(v).trim().toLowerCase();

  const num = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  };

  const handlePasswordChange = async () => {
    const email = formData.Email || localStorage.getItem("email");

    if (!email) return alert("No logged-in user found.");

    if (passwordData.new !== passwordData.confirm) {
      return alert("Passwords do not match.");
    }

    try {
      const res = await axios.post(`${API_BASE_URL}/api/change-app-password`, {
        email,
        currentPassword: passwordData.current,
        newPassword: passwordData.new,
      });

      alert(res.data.message || "Password updated.");
      setShowPasswordModal(false);
      setPasswordData({ current: "", new: "", confirm: "" });
    } catch (err) {
      alert(
        err.response?.data?.message ||
          "An error occurred while changing password.",
      );
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "JambScore" && isJambRequired) {
      let v = value.replace(/\D/g, "");
      if (v.length > 3) v = v.slice(0, 3);
      if (+v > 400) v = "400";
      setFormData({ ...formData, [name]: v });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleInstitutionChange = (index, e) => {
    const updated = [...institutions];
    updated[index][e.target.name] = e.target.value;
    setInstitutions(updated);
  };

  const addInstitution = () => {
    setInstitutions([
      ...institutions,
      {
        InstitutionName: "",
        QualificationObtained: "",
        StartYear: "",
        EndYear: "",
      },
    ]);
  };

  const removeInstitution = (idx) => {
    if (institutions.length === 1) return;
    setInstitutions(institutions.filter((_, i) => i !== idx));
  };

  const handleExamChange = (index, e) => {
    const updated = [...exams];
    const { name, value } = e.target;

    if (name in updated[index].SubjectsAndGrades) {
      updated[index].SubjectsAndGrades[name] = value;
    } else {
      updated[index][name] = value;
    }

    setExams(updated);
  };

  const addExam = () => {
    if (exams.length < 2) {
      setExams([
        ...exams,
        {
          ExamName: "",
          Sittings: exams.length + 1,
          SubjectsAndGrades: makeSAG(),
        },
      ]);
    } else {
      alert("Only two sittings are allowed.");
    }
  };

  const removeExam = (idx) => {
    if (exams.length === 1) return;
    const remaining = exams.filter((_, i) => i !== idx);
    const reindexed = remaining.map((item, index) => ({
      ...item,
      Sittings: index + 1,
    }));
    setExams(reindexed);
  };

  const handleDocumentsChange = (index, e) => {
    const file = e.target.files[0];
    const newDocuments = [...documents];
    newDocuments[index] = {
      file,
      preview: file ? URL.createObjectURL(file) : null,
    };
    setDocuments(newDocuments);
  };

  const addDocumentField = () => {
    setDocuments([...documents, { file: null, preview: null }]);
  };

  const validateForm = () => {
    for (const key in formData) {
      const isJambField = key.toLowerCase().startsWith("jamb");

      if (
        (formData[key] === "" || formData[key] === null) &&
        (!isJambField || isJambRequired)
      ) {
        alert(`Please fill in the ${key.replace(/([A-Z])/g, " $1")} field.`);
        return false;
      }
    }

    if (isJambRequired) {
      const dobDate = formData.DateOfBirth
        ? new Date(`${formData.DateOfBirth}T00:00:00`)
        : null;

      if (!dobDate || Number.isNaN(dobDate.getTime())) {
        alert("Please select your date of birth.");
        return false;
      }

      if (!ageCutoffDate || dobDate > ageCutoffDate) {
        alert(
          "You must be at least 16 years old by September 30 of the application year.",
        );
        return false;
      }
    }

    for (let i = 0; i < institutions.length; i++) {
      const inst = institutions[i];
      if (
        !inst.InstitutionName ||
        !inst.QualificationObtained ||
        !inst.StartYear ||
        !inst.EndYear
      ) {
        alert(`Please fill in all fields for Institution ${i + 1}.`);
        return false;
      }
    }

    for (let i = 0; i < exams.length; i++) {
      const exam = exams[i];
      if (!exam.ExamName) {
        alert(`Please enter Exam Name for sitting ${i + 1}.`);
        return false;
      }

      const grades = exam.SubjectsAndGrades;

      if (isMedSocial) {
        for (let j = 0; j < 8; j++) {
          const subj = grades[`subject${j}`]?.trim();
          const grd = grades[`grade${j}`]?.trim();
          if ((subj && !grd) || (!subj && grd)) {
            alert(
              `Please fill both subject and grade for slot ${
                j + 1
              } in sitting ${i + 1}.`,
            );
            return false;
          }
        }
      } else {
        for (const subject in grades) {
          if (!grades[subject]) {
            alert(`Please select a grade for ${subject} in sitting ${i + 1}.`);
            return false;
          }
        }
      }
    }

    if (!passportPhoto) {
      alert("Please upload your passport photo.");
      return false;
    }

    return true;
  };

  function handleLogout() {
    localStorage.clear();
    navigate("/applicant-login", { replace: true });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const programmeValue = String(
      formData.programme || localStorage.getItem("programme") || "",
    ).trim();

    if (!programmeValue) {
      alert("Programme is missing. Please go back and select a programme.");
      return;
    }

    const form = new FormData();

    Object.entries(formData).forEach(([key, val]) => {
      if (key !== "programme") {
        form.append(key, val);
      }
    });

    form.append("programme", programmeValue);
    form.append("passportPhoto", passportPhoto);
    form.append("institutions", JSON.stringify(institutions));
    form.append("exams", JSON.stringify(exams));

    documents.forEach((docObj) => {
      if (docObj.file) form.append("documents", docObj.file);
    });

    setIsSubmitting(true);

    try {
      const { data: saved } = await axios.post(
        `${API_BASE_URL}/api/appbiodata`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      const { passportUrl, applicationNo } = saved;

      const biodataDisplay = {
        ...formData,
        programme: programmeValue,
        institutions,
        exams,
        applicationNo,
        passportUrl,
        documents: documents.map((doc) => doc.preview),
      };

      localStorage.setItem("biodata", JSON.stringify(biodataDisplay));
      setSubmittedBiodata(biodataDisplay);
      setSubmitted(true);
    } catch (error) {
      console.error(error);
      alert("Submission failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return <ApplicationSuccess biodata={submittedBiodata} />;
  }

  return (
    <div className="biodata-form-container">
      <div
        className="subschool-title-banner"
        style={{
          marginTop: "-150px",
          marginBottom: "2rem",
        }}
      >
        <h1 className="subschool-title">{programme}</h1>
      </div>

      <div className="card-row">
        <div className="card active-card">Fill Form</div>

        <div className="card" onClick={() => setShowPasswordModal(true)}>
          Change Password
        </div>

        <div
          style={{
            backgroundColor: "#e74c3c !important",
            color: "white",
          }}
          className="card logout-card"
          onClick={handleLogout}
        >
          Logout
        </div>
      </div>

      <form className="biodata-form" onSubmit={handleSubmit}>
        {programme && (
          <h3 className="programme-header">
            Fill form to register for <strong>{programme}</strong>
          </h3>
        )}

        <strong>
          <h3>
            KINDLY NOTE THAT ANY WRONG INFORMATION PROVIDED IS A PUNISHABLE
            OFFENCE AND AUTOMATICALLY LEADS TO DISQUALIFICATION
          </h3>
        </strong>

        <h2>Biodata Form</h2>

        <section>
          <h3>Personal Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Surname</label>
              <input name="LastName" onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>First Name</label>
              <input name="FirstName" onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Middle Name</label>
              <input name="MiddleName" onChange={handleChange} required />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Gender</label>
              <select name="Gender" onChange={handleChange} required>
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div className="form-group">
              <label>Date of Birth</label>
              <DatePicker
                selected={dob}
                onChange={(date) => {
                  if (!date) return;

                  if (isJambRequired && ageCutoffDate && date > ageCutoffDate) {
                    alert(
                      "You must be at least 16 years old by September 30 of the application year.",
                    );
                    return;
                  }

                  setDob(date);
                  setFormData({
                    ...formData,
                    DateOfBirth: formatDateToYMD(date),
                  });
                }}
                dateFormat="dd-MM-yyyy"
                placeholderText="Select your date of birth"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className="date-picker-input"
                required
                maxDate={isJambRequired ? ageCutoffDate : undefined}
                minDate={new Date("1900-01-01")}
                filterDate={(date) =>
                  !isJambRequired || !ageCutoffDate
                    ? true
                    : date <= ageCutoffDate
                }
                yearDropdownItemNumber={100}
                scrollableYearDropdown
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="Email"
                value={formData.Email}
                readOnly
                style={{ backgroundColor: "#eee", cursor: "not-allowed" }}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone Number</label>
              <div
                style={{ fontSize: "0.9rem", marginBottom: 6, color: "#555" }}
              >
                Please enter your valid working WhatsApp number.
              </div>
              <input name="PhoneNumber" onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Marital Status</label>
              <input name="MaritalStatus" onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Maiden Name</label>
              <input name="MaidenName" onChange={handleChange} required />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>State of Origin</label>
              <input name="StateOfOrigin" onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Local Government</label>
              <input name="LocalGovernment" onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Nationality</label>
              <input name="Nationality" onChange={handleChange} required />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Religion</label>
              <input name="Religion" onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Denomination</label>
              <input
                name="Denomination"
                onChange={handleChange}
                placeholder="If none indicate by typing 'none'"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Co-Curricular Activities</label>
              <input
                name="CoCurricularActivities"
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Permanent Address</label>
              <textarea
                name="PermanentAddress"
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </section>

        <section>
          <h3>Next of Kin</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Next of Kin</label>
              <input name="NextOfKin" onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Next of Kin Phone</label>
              <input name="NextOfKinPhone" onChange={handleChange} required />
            </div>

            <div className="form-group full-width">
              <label>Next of Kin Address</label>
              <input name="NextOfKinAddress" onChange={handleChange} required />
            </div>
          </div>
        </section>

        {isJambRequired && (
          <section>
            <h3>JAMB Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>JAMB Registration Number</label>
                <input name="JambRegNumber" onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label>Year of Exam</label>
                <input name="JambYear" onChange={handleChange} required />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Examination Number</label>
                  <input
                    name="JambExamNumber"
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>JAMB Score</label>
                  <input
                    type="number"
                    name="JambScore"
                    value={formData.JambScore}
                    onChange={handleChange}
                    min={jambMin}
                    max={400}
                    placeholder={`${jambMin} - 400`}
                    required
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        <section>
          <h3>Institutions Attended</h3>

          {institutions.map((inst, idx) => (
            <div className="exam-block" key={idx} style={{ marginBottom: 20 }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Institution Name</label>
                  <input
                    name="InstitutionName"
                    value={inst.InstitutionName}
                    onChange={(e) => handleInstitutionChange(idx, e)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Qualification</label>
                  <input
                    name="QualificationObtained"
                    value={inst.QualificationObtained}
                    onChange={(e) => handleInstitutionChange(idx, e)}
                    required
                  />
                </div>

                {idx > 0 && (
                  <button
                    type="button"
                    onClick={() => removeInstitution(idx)}
                    style={{ marginLeft: 8 }}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Year</label>
                  <input
                    type="date"
                    name="StartYear"
                    value={inst.StartYear}
                    onChange={(e) => handleInstitutionChange(idx, e)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Year</label>
                  <input
                    type="date"
                    name="EndYear"
                    value={inst.EndYear}
                    onChange={(e) => handleInstitutionChange(idx, e)}
                    required
                  />
                </div>
              </div>
            </div>
          ))}

          <button type="button" onClick={addInstitution}>
            + Add More
          </button>
        </section>

        <section>
          <h3>Examinations Passed</h3>

          {exams.map((exam, idx) => (
            <div key={idx} className="exam-block">
              <div className="form-row">
                <div className="form-group">
                  <label>Exam Name</label>
                  <input
                    name="ExamName"
                    value={exam.ExamName}
                    onChange={(e) => handleExamChange(idx, e)}
                    required
                  />
                </div>

                {idx > 0 && (
                  <button
                    type="button"
                    onClick={() => removeExam(idx)}
                    style={{ marginLeft: 8 }}
                  >
                    Remove
                  </button>
                )}
              </div>

              <p>Sitting {exam.Sittings}</p>

              <div className="form-row">
                {isMedSocial
                  ? Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="form-group">
                        <label>Subject #{i + 1}</label>
                        <input
                          type="text"
                          name={`subject${i}`}
                          placeholder="Type subject"
                          value={exam.SubjectsAndGrades[`subject${i}`]}
                          onChange={(e) => handleExamChange(idx, e)}
                          required
                        />

                        <label>Grade</label>
                        <select
                          name={`grade${i}`}
                          value={exam.SubjectsAndGrades[`grade${i}`]}
                          onChange={(e) => handleExamChange(idx, e)}
                          required
                        >
                          <option value="">Select Grade</option>
                          {gradeOptions.map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))
                  : Object.keys(exam.SubjectsAndGrades).map((subject) => (
                      <div key={subject} className="form-group">
                        <label>{subject}</label>
                        <select
                          name={subject}
                          value={exam.SubjectsAndGrades[subject]}
                          onChange={(e) => handleExamChange(idx, e)}
                          required
                        >
                          <option value="">Select Grade</option>
                          {gradeOptions.map((grade) => (
                            <option key={grade} value={grade}>
                              {grade}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
              </div>
            </div>
          ))}

          <button type="button" onClick={addExam}>
            + Add Exam
          </button>
        </section>

        <section>
          <h3>Uploads</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Upload Passport Photo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  setPassportPhoto(e.target.files[0]);
                }}
              />
              {passportPhoto && (
                <img
                  src={URL.createObjectURL(passportPhoto)}
                  alt="Passport Preview"
                  style={{
                    width: "120px",
                    height: "120px",
                    marginTop: "10px",
                    objectFit: "cover",
                    borderRadius: "5px",
                  }}
                />
              )}
            </div>
          </div>

          <div className="form-row">
            <h3>Supporting Documents</h3>

            {documents.map((doc, index) => (
              <div
                className="form-group"
                key={index}
                style={{ marginBottom: "10px" }}
              >
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => handleDocumentsChange(index, e)}
                  required
                />
                {doc.preview && (
                  <div style={{ marginTop: "5px" }}>
                    {doc.file?.type?.startsWith("image/") ? (
                      <img
                        src={doc.preview}
                        alt={`Document ${index + 1}`}
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <a
                        href={doc.preview}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Preview Document {index + 1}
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}

            <p
              style={{
                fontSize: "1.5rem",
                color: "#000000ff",
                marginBottom: "10px",
                fontStyle: "italic",
                fontWeight: "400",
                lineHeight: "1.5",
              }}
            >
              Please ensure that each document you upload is no larger than{" "}
              <strong>1MB</strong>.
            </p>

            <button
              type="button"
              onClick={addDocumentField}
              style={{
                padding: "5px 10px",
                background: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                marginTop: "10px",
              }}
            >
              + Add Document
            </button>
          </div>
        </section>

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Processing..." : "Submit Bio-Data"}
        </button>
      </form>

      {isSubmitting && (
        <div className="loading-overlay">
          <div className="spinner" />
        </div>
      )}

      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Change Password</h2>
            <input
              type="password"
              placeholder="Current Password"
              value={passwordData.current}
              onChange={(e) =>
                setPasswordData({ ...passwordData, current: e.target.value })
              }
            />
            <input
              type="password"
              placeholder="New Password"
              value={passwordData.new}
              onChange={(e) =>
                setPasswordData({ ...passwordData, new: e.target.value })
              }
            />
            <input
              type="password"
              placeholder="Confirm New Password"
              value={passwordData.confirm}
              onChange={(e) =>
                setPasswordData({ ...passwordData, confirm: e.target.value })
              }
            />
            <div className="modal-buttons">
              <button className="modal-btn" onClick={handlePasswordChange}>
                Submit
              </button>
              <button
                className="modal-btn"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BiodataForm;
